import type { OasWebPageGroup, TableEndpoint } from '@ipfabric/utils';
import { ApiTablePage } from 'components/ApiTablePage/ApiTablePage';
import { apiTablePageComponentMap } from 'components/ApiTablePage/apiTablePageComponentMap';
import {
  type ConnectedApiTableProps,
  createConnectedApiTable,
} from 'components/ConnectedApiTable/ConnectedApiTable';
import type { RoutesTreeItem } from 'containers/App/Routes/RoutesTreeItem';
import { createTableApiScopeRequirement } from 'utils/rbac/helpers/createTableApiScopeRequirement';
import { PageBookmark } from '../PageBookmark/PageBookmark';
import { apiTableActionsColumnsMap } from './ApiTableActionsColumnMap';
import { apiTableColumnOverridesMap } from './ApiTableColumnOverridesMap';
import { apiTableNoDataBoxMap } from './ApiTableNoDataBoxMap';

const applyApiTableActionsColumn = (
  tableEndpoint: TableEndpoint<any>,
): TableEndpoint<any> => {
  const actionsColumn = apiTableActionsColumnsMap[tableEndpoint.id];

  return actionsColumn
    ? {
        ...tableEndpoint,
        columns: [...tableEndpoint.columns, actionsColumn],
      }
    : tableEndpoint;
};

/**
 * Customize individual OAS columns in place, keeping their position, filter and
 * sort — unlike the actions column, which is always appended at the end.
 */
const applyApiTableColumnOverrides = (
  tableEndpoint: TableEndpoint<any>,
): TableEndpoint<any> => {
  const overrides = apiTableColumnOverridesMap[tableEndpoint.id];

  return overrides
    ? {
        ...tableEndpoint,
        columns: tableEndpoint.columns.map((column) => {
          const override = overrides[column.key];
          return override ? override(column) : column;
        }),
      }
    : tableEndpoint;
};

export const createApiTableRouteInfo =
  (group: OasWebPageGroup) =>
  (tableEndpoint: TableEndpoint<any>): RoutesTreeItem => {
    const tableEndpointForPage = applyApiTableActionsColumn(
      applyApiTableColumnOverrides(tableEndpoint),
    );
    const tableComponent = createConnectedApiTable({
      tableEndpoint: tableEndpointForPage,
    });

    const apiTableFromOasProps: ConnectedApiTableProps = {
      enableIntentEditor: true,
      noDataBox: apiTableNoDataBoxMap[tableEndpoint.id],
      titleEndAdornment:
        group === 'technology' && tableEndpoint.webPath ? (
          <PageBookmark urlPath={tableEndpoint.webPath} className='ml-3xs' />
        ) : undefined,
    };

    const getPageElement = () => {
      const PageComponent = apiTablePageComponentMap[tableEndpoint.id];
      if (PageComponent) {
        return (
          <PageComponent
            renderApiTablePage={(additionalApiTableProps) => (
              <ApiTablePage
                key={tableEndpointForPage.id}
                endpoint={tableEndpointForPage}
                additionalApiTableProps={{
                  ...apiTableFromOasProps,
                  ...additionalApiTableProps,
                }}
              />
            )}
          />
        );
      }
      return (
        <ApiTablePage
          key={tableEndpointForPage.id}
          endpoint={tableEndpointForPage}
          additionalApiTableProps={apiTableFromOasProps}
        />
      );
    };

    return {
      // replace with tableEndpoint.apiEndpoint when newApiTables feature is released
      // and all usages from routes are rewritten
      apiEndpoint: tableEndpoint.apiEndpoint,
      requiredApiScope: createTableApiScopeRequirement(
        tableEndpoint.apiEndpoint,
      ),
      element: getPageElement(),
      path: tableEndpoint.webPath,
      tableComponent,
      tableEndpoint,
    };
  };
